import { useState } from "react";
import { useQuery } from "@apollo/client";
import _ from "lodash";

import AssetNameplate from "../AssetNameplate";
import CreateInstalledBaseEventModal from "../CreateInstalledBaseEventModal";
import { InstalledBaseNodeEventContext } from "../../graphql/queries/installedBaseNodeEventContext";
import { ManualEventType } from "../../codegen/graphql";
import { resolveNameplateEventAction } from "../../utils/resolveNameplateEventAction";

interface Props {
    assetLocationId: string;
    installedBaseNodeId: string;
    assetId?: string;
    onOpenAsset: () => void;
    /** Called after a successful removal so the column/tree can refresh. */
    onRemoved?: () => void;
    /** IBM-586: trashed assets only offer Restore + Delete — no remove event. */
    hideRemoveAction?: boolean;
}

/**
 * Wraps {@link AssetNameplate} for the installed-base tree column: fetches the node's available
 * events and surfaces the matching event action — "Remove asset" when removal is allowed, or
 * "On-site Intervention" instead for Systems-and-solutions assets (see
 * {@link resolveNameplateEventAction}). Both open the same event workflow as the asset dashboard.
 */
const ColumnAssetNameplate = ({ assetLocationId, installedBaseNodeId, assetId, onOpenAsset, onRemoved, hideRemoveAction }: Props) => {
    const [modalOpen, setModalOpen] = useState(false);

    const { data } = useQuery(InstalledBaseNodeEventContext, {
        variables: { id: installedBaseNodeId },
        skip: !installedBaseNodeId,
    });
    const availableEvents: ManualEventType[] = _.get(data, "installedBaseNode.availableInstalledBaseEvents", []);
    const productCategoryUrn = _.get(data, "installedBaseNode.assetLocation.asset.product.productCategory.urn");
    const eventAction = (hideRemoveAction || !assetId)
        ? null
        : resolveNameplateEventAction(availableEvents, productCategoryUrn);
    const isRemove = eventAction === ManualEventType.ASSET_REMOVE;

    return (
        <>
            <AssetNameplate
                assetLocationId={assetLocationId}
                onOpenAsset={onOpenAsset}
                onRemoveAsset={isRemove ? () => setModalOpen(true) : undefined}
                onOnsiteIntervention={eventAction === ManualEventType.ASSET_ONSITE_INTERVENTION ? () => setModalOpen(true) : undefined}
            />

            {modalOpen && assetId && eventAction &&
                <CreateInstalledBaseEventModal
                    assetId={assetId}
                    assetLocationId={assetLocationId}
                    installedBaseNodeId={installedBaseNodeId}
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    initialEventType={eventAction}
                    // The modal's default refetch only covers the asset detail/breadcrumb/event list.
                    // Removal changes the tree itself, so also refetch the column list + parent-node
                    // queries — by name this refreshes every currently-mounted tree column. An on-site
                    // intervention leaves the tree untouched, so the default refetch suffices there.
                    refetchQueries={isRemove ? [
                        "InstalledBaseNodeList",
                        "InstalledBaseRootNodeList",
                        "InstalledBaseNode",
                        "AssetBreadcrumbPath",
                        "AssetNameplateInfo",
                        "InstalledBaseEventList",
                    ] : undefined}
                    onSuccess={isRemove ? () => onRemoved?.() : undefined}
                />
            }
        </>
    );
};

export default ColumnAssetNameplate;
